import { pointInOrOnPolygon, type Vec2 } from '@indusketch/geometry';
import { buildingCorners } from './building.js';
import { hasFlatRoof, levelElevation, roofTop, type LevelDoc } from './office.js';
import type { Building, Railing } from './types.js';

/** A railing drawn along an edge may lie this far outside the surface and still stand on it. */
const ON_EDGE = 0.3;

const covers = (poly: readonly Vec2[], points: readonly Vec2[]) =>
  points.every((p) => pointInOrOnPolygon(p, poly, ON_EDGE));

export type RailingMount = Pick<Railing, 'mount' | 'level' | 'buildingId' | 'elevation'>;

/** Flat roofs lying under all points, highest first. */
export function roofsUnder(doc: Pick<LevelDoc, 'buildings'>, points: readonly Vec2[]): Building[] {
  return Object.values(doc.buildings)
    .filter((b) => hasFlatRoof(b) && covers(buildingCorners(b), points))
    .sort((a, b) => roofTop(b) - roofTop(a));
}

/**
 * Where a railing drawn on `viewLevel` stands. On an upper level it stays there; drawn on the
 * EG over a mezzanine it goes onto the mezzanine, over an office onto its roof, else on the
 * ground (ramps, hall floor). Hall roofs are chosen in the panel, since railings on the hall
 * floor are common.
 */
export function detectRailingMount(
  doc: LevelDoc,
  points: readonly Vec2[],
  viewLevel: number,
): RailingMount {
  const onLevel = (level: number): RailingMount => ({
    mount: 'level',
    level,
    buildingId: null,
    elevation: 0,
  });
  if (viewLevel >= 1) return onLevel(viewLevel);
  if (Object.values(doc.mezzanines ?? {}).some((m) => covers(m.polygon, points))) return onLevel(1);
  const office = roofsUnder(doc, points).find((b) => b.office);
  if (office) return { mount: 'roof', level: 0, buildingId: office.id, elevation: 0 };
  return onLevel(0);
}

const middle = (points: readonly Vec2[]): Vec2 => ({
  x: points.reduce((s, p) => s + p.x, 0) / Math.max(points.length, 1),
  y: points.reduce((s, p) => s + p.y, 0) / Math.max(points.length, 1),
});

/** Absolute height of the surface the railing stands on. */
export function railingBase(doc: LevelDoc, r: Pick<Railing, 'points'> & RailingMount): number {
  if (r.mount === 'custom') return r.elevation;
  if (r.mount === 'roof') {
    const b = r.buildingId ? doc.buildings[r.buildingId] : undefined;
    return b ? roofTop(b) : 0;
  }
  return r.level > 0 ? levelElevation(doc, r.level, middle(r.points)) : 0;
}
